import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, merge, of } from 'rxjs';
import { concatMap, map, shareReplay, take, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AuthService } from '@services/auth.service';
import { VendorOrder, VendorOrders } from '@models/vendor-order';
import { Order } from '@models/order';
import { ProductHistory } from '@models/product';
import { ApiResponse } from '@classes/api-response';
import { Params } from '@angular/router';
import { AuthResponse, Payable, PayableGroup } from '@models/api.model';

@Injectable({
  providedIn: 'root',
})
export class VendorOrderService {
  private base_url = environment.base_url + '/vendorOrder';
  private vendorOrderCountsCache = new Map<
    string,
    Observable<ApiResponse<any[]>>
  >();
  private vendorOrderCountsSnapshot = new Map<string, ApiResponse<any[]>>();

  private productHistoryCache = new Map<string, Observable<ProductHistory[]>>();

  constructor(public http: HttpClient, private auth: AuthService) {}

  getVendorOrders(
    type: string,
    ids: string[] = []
  ): Observable<ApiResponse<VendorOrders[]>> {
    const store = this.auth.getStore();
    const body = ids;
    return this.http
      .put(`${this.base_url}/store/${store}/type/${type}`, body)
      .pipe(
        take(1),
        tap((res: ApiResponse<VendorOrders[]>) => {
          this.auth.updateToken(res);
        })
      );
  }

  getBorrowingVendorOrders(borrowingStore: any): Observable<VendorOrders[]> {
    const store = this.auth.getStore();
    return this.http
      .get(`${this.base_url}/borrow/store/${store}/${borrowingStore}`)
      .pipe(
        take(1),
        tap((res: ApiResponse<VendorOrders[]>) => {
          this.auth.updateToken(res);
        }),
        map((res: ApiResponse<VendorOrders[]>) => res.vendorOrders ?? [])
      );
  }

  getCachedVendorOrderCounts(type: string): ApiResponse<any[]> | null {
    const store = this.auth.getStore();
    return this.vendorOrderCountsSnapshot.get(`${store}:${type}`) ?? null;
  }

  getDistributorVendorOrderCounts(
    type: string
  ): Observable<ApiResponse<any[]>> {
    const store = this.auth.getStore();
    const cacheKey = `${store}:${type}`;
    const cached = this.vendorOrderCountsSnapshot.get(cacheKey);

    if (!this.vendorOrderCountsCache.has(cacheKey)) {
      const request$ = this.http
        .get(`${this.base_url}/counts/store/${store}/type/${type}`)
        .pipe(
          tap((res: ApiResponse<VendorOrders[]>) => {
            this.auth.updateToken(res);
            this.vendorOrderCountsSnapshot.set(cacheKey, res);
          }),
          shareReplay({ bufferSize: 1, refCount: true })
        );
      this.vendorOrderCountsCache.set(cacheKey, request$);
    }

    const network$ = this.vendorOrderCountsCache.get(cacheKey)!;
    return cached ? merge(of(cached), network$) : network$;
  }

  clearVendorOrderCountsCache(type?: string) {
    if (type) {
      const store = this.auth.getStore();
      const cacheKey = `${store}:${type}`;
      this.vendorOrderCountsCache.delete(cacheKey);
      this.vendorOrderCountsSnapshot.delete(cacheKey);
      return;
    }
    this.vendorOrderCountsCache.clear();
    this.vendorOrderCountsSnapshot.clear();
  }

  private invalidatePayablesCountsCache(): void {
    this.clearVendorOrderCountsCache('all');
  }

  getVendorPayablesList(
    type: string,
    vendorIds: Array<string>,
    params: Params = {}
  ): Observable<Payable[]> {
    const store = this.auth.getStore();
    const listRequest = this.http
      .post(
        `${environment.base_url}/invoices/store/${store}/type/${type || 'unpaid'}`,
        vendorIds,
        {
          params,
        }
      )
      .pipe(
        tap((res: AuthResponse<Payable[]>) => {
          this.auth.updateToken(res);
        }),
        map((res: ApiResponse<Payable[]>) => res.data)
      );

    if (type === 'paid') {
      return this.getVendorInvoices(vendorIds, params).pipe(
        take(1),
        concatMap(() => listRequest)
      );
    }

    return listRequest.pipe(take(1));
  }

  getVendorInvoices(
    vendorIds: Array<string>,
    params: Params = {}
  ): Observable<Array<PayableGroup>> {
    const store = this.auth.getStore();
    return this.http
      .post(`${environment.base_url}/invoices/store/${store}/paid-invoices`, {
        vendorIds,
        ...params,
      })
      .pipe(
        take(1),
        map((res: ApiResponse<Array<PayableGroup>>) => res.data)
      );
  }

  getVendorVendorOrders(
    type: string,
    vendorId: string,
    params: Params = {}
  ): Observable<ApiResponse<VendorOrders[]>> {
    const store = this.auth.getStore();
    return this.http
      .get(`${this.base_url}/vendor/${vendorId}/store/${store}/type/${type}`, {
        params,
      })
      .pipe(
        take(1),
        tap((res: ApiResponse<VendorOrders[]>) => this.auth.updateToken(res))
      );
  }

  getDistributorVendorOrders(
    type: string,
    ids: any[] = [],
    distributorId?: string,
    params: Params = {}
  ): Observable<VendorOrder[]> {
    const store = this.auth.getStore();
    const body = ids;
    let url = `${this.base_url}/distributor/store/${store}/type/${type}`;
    if (distributorId) {
      url += `/distributor/${distributorId}`;
    }
    return this.http.put(url, body, { params }).pipe(
      take(1),
      map((res: ApiResponse<VendorOrder[]>) => res.vendorOrders)
    );
  }

  getBorrowedVendorOrders(
    type: string,
    borrowingFromStore: string
  ): Observable<VendorOrder[]> {
    const store = this.auth.getStore();
    return this.http
      .get(
        `${this.base_url}/borrowed/store/${store}/${borrowingFromStore}/type/${type}`
      )
      .pipe(
        take(1),
        map((res: ApiResponse<VendorOrder[]>) => res.vendorOrders)
      );
  }

  getVendorOrdersById(
    type: string,
    ids: any[] = []
  ): Observable<ApiResponse<VendorOrders[]>> {
    const store = this.auth.getStore();
    const body = ids;
    return this.http
      .put(`${this.base_url}/store/${store}/type/${type}`, body)
      .pipe(
        take(1),
        tap((res: ApiResponse<VendorOrders[]>) => this.auth.updateToken(res))
      );
  }

  getVendorOrderById(type: string, ids: any[] = []): Observable<VendorOrders> {
    return this.getVendorOrdersById(type, ids).pipe(
      map((res: ApiResponse<VendorOrders[]>) => res.vendorOrders[0])
    );
  }

  getVendorOrder(vendorOrderId: any): Observable<VendorOrder> {
    return this.http.get<VendorOrder>(`${this.base_url}/${vendorOrderId}`);
  }

  getProductHistory(productId: any): Observable<ProductHistory[]> {
    const cacheKey = `product:${productId}`;
    if (!this.productHistoryCache.has(cacheKey)) {
      this.productHistoryCache.set(
        cacheKey,
        this.http
          .get(`${this.base_url}/productHistory/${productId}`)
          .pipe(
            take(1),
            map((res: ApiResponse<ProductHistory[]>) => res.productHistory),
            shareReplay(1)
          )
      );
    }
    return this.productHistoryCache.get(cacheKey)!;
  }

  getPartHistory(productId: any): Observable<ProductHistory[]> {
    const cacheKey = `part:${productId}`;
    if (!this.productHistoryCache.has(cacheKey)) {
      this.productHistoryCache.set(
        cacheKey,
        this.http.get(`${this.base_url}/partHistory/${productId}`).pipe(
          take(1),
          map((res: ApiResponse<ProductHistory[]>) => res.productHistory),
          shareReplay(1)
        )
      );
    }
    return this.productHistoryCache.get(cacheKey)!;
  }

  clearProductHistoryCache(productId?: string | number) {
    if (productId == null) {
      this.productHistoryCache.clear();
      return;
    }
    const id = productId.toString();
    this.productHistoryCache.delete(`product:${id}`);
    this.productHistoryCache.delete(`part:${id}`);
  }

  private invalidateProductHistoryForProducts(
    products?: Array<{ product?: { _id?: string | number } }> | null
  ): void {
    products?.forEach((entry) => {
      const productId = entry?.product?._id;
      if (productId != null && productId !== '') {
        this.clearProductHistoryCache(productId);
      }
    });
  }

  private invalidateProductHistoryForVendorOrder(
    vendorOrder?: VendorOrder | Partial<VendorOrder> | null
  ): void {
    if (!vendorOrder) {
      return;
    }

    this.invalidateProductHistoryForProducts(vendorOrder.products);
    vendorOrder.framesRemoved?.forEach((productId) =>
      this.clearProductHistoryCache(productId)
    );
  }

  invalidateProductHistoryForOrder(order?: Order | Partial<Order> | null): void {
    if (!order) {
      return;
    }

    this.invalidateProductHistoryForProducts(order.products);
    order.framesRemoved?.forEach((productId) =>
      this.clearProductHistoryCache(productId)
    );
  }

  addVendorOrderProduct(order: Order): Observable<VendorOrder> {
    const body = order;
    return this.http.post(`${this.base_url}/product`, body).pipe(
      take(1),
      tap((res: ApiResponse<VendorOrder>) => {
        this.invalidateProductHistoryForOrder(order);
        this.invalidateProductHistoryForVendorOrder(res.vendorOrder);
        this.invalidatePayablesCountsCache();
      }),
      map((res: ApiResponse<VendorOrder>) => res.vendorOrder)
    );
  }

  addProductIdToVendorOrder(order: Order): Observable<VendorOrder> {
    const body = order;
    return this.http.post(`${this.base_url}/productId`, body).pipe(
      take(1),
      tap((res: ApiResponse<VendorOrder>) => {
        this.invalidateProductHistoryForOrder(order);
        this.invalidateProductHistoryForVendorOrder(res.vendorOrder);
        this.invalidatePayablesCountsCache();
      }),
      map((res: ApiResponse<VendorOrder>) => res.vendorOrder)
    );
  }

  addVendorOrder(vendorOrder: VendorOrder): Observable<VendorOrder> {
    vendorOrder.store = this.auth.getStore();
    const body = vendorOrder;
    return this.http.post(`${this.base_url}`, body).pipe(
      take(1),
      tap((res: ApiResponse<VendorOrder>) => {
        this.invalidateProductHistoryForVendorOrder(vendorOrder);
        this.invalidateProductHistoryForVendorOrder(res.vendorOrder);
        this.invalidatePayablesCountsCache();
      }),
      map((res: ApiResponse<VendorOrder>) => res.vendorOrder)
    );
  }

  updateVendorOrder(
    vendorOrderId: any,
    vendorOrder: VendorOrder
  ): Observable<VendorOrder> {
    vendorOrder.store = this.auth.getStore();
    const body = vendorOrder;
    return this.http.put(`${this.base_url}/${vendorOrderId}`, body).pipe(
      take(1),
      tap((res: ApiResponse<VendorOrder>) => {
        this.invalidateProductHistoryForVendorOrder(vendorOrder);
        this.invalidateProductHistoryForVendorOrder(res.vendorOrder);
        this.invalidatePayablesCountsCache();
      }),
      map((res: ApiResponse<VendorOrder>) => res.vendorOrder)
    );
  }

  updateVendorOrderById(
    vendorOrderId: string,
    vendorOrder: Partial<VendorOrder>
  ): Observable<VendorOrder> {
    vendorOrder.store = this.auth.getStore();
    const body = vendorOrder;
    return this.http.put(`${this.base_url}/v2/${vendorOrderId}`, body).pipe(
      take(1),
      tap((res: AuthResponse<VendorOrder>) => {
        this.auth.updateToken(res);
        this.invalidateProductHistoryForVendorOrder(vendorOrder);
        this.invalidateProductHistoryForVendorOrder(res.data);
        this.invalidatePayablesCountsCache();
      }),
      map((res: ApiResponse<VendorOrder>) => res.data)
    );
  }

  deleteVendorOrder(vendorOrderId: any): Observable<VendorOrder> {
    return this.http.delete(`${this.base_url}/${vendorOrderId}`).pipe(
      take(1),
      tap((res: ApiResponse<VendorOrder>) => {
        this.invalidateProductHistoryForVendorOrder(res.vendorOrder);
        this.invalidatePayablesCountsCache();
      }),
      map((res: ApiResponse<VendorOrder>) => res.vendorOrder)
    );
  }

  deleteProductFromInvoice(
    productId: any,
    orderId: any,
    vendorOrderId: any
  ): Observable<VendorOrder> {
    return this.http
      .delete(
        `${this.base_url}/deleteProductFromInvoice/${productId}/${orderId}/${vendorOrderId}`
      )
      .pipe(
        tap((res: ApiResponse<VendorOrder>) => {
          this.clearProductHistoryCache(productId);
          this.invalidateProductHistoryForVendorOrder(res.vendorOrder);
          this.invalidatePayablesCountsCache();
        }),
        map((res: ApiResponse<VendorOrder>) => res.vendorOrder)
      );
  }

  getVendorOrderByInvoiceNumber(
    vendorId: string,
    invoiceNumber: string | number,
    store?: string | number
  ): Observable<any> {
    const normalized = String(invoiceNumber ?? '').trim();
    const storeQuery =
      store != null && store !== '' ? `?store=${encodeURIComponent(store)}` : '';
    return this.http
      .get(
        `${this.base_url}/vendor/${vendorId}/invoice/${encodeURIComponent(normalized)}${storeQuery}`
      )
      .pipe(map((res: ApiResponse<VendorOrders[]>) => res.orders));
  }
}
